/**
 * SendDealEmailPanel — compose and send an email about a deal.
 *
 * The mail goes out through one of the user's OWN mailboxes connected in
 * the Email arche (its SMTP server, its address), not through any platform
 * sender — so the reply comes back to the rep's real inbox and the message
 * lands in their real Sent folder. With no mailbox connected there is
 * nothing to send as, so the panel says so and links to the Email arche
 * rather than offering a send that would fail at the server.
 *
 * On a successful send the server writes an `email` activity on the deal,
 * which is what the timeline and the "last activity" sort read from — so
 * sending from here is also what clears the deal's stale alert.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Send, X } from 'lucide-react';
import {
  useFiasLeadsContacts,
  useFiasLeadsDealContacts,
  useFiasLeadsEmailTemplates,
  useFiasLeadsMailboxes,
  useSendFiasLeadsDealEmail,
} from '../hooks/useFiasLeads';
import type { FiasLeadsContactDto } from '@/lib/api';

interface Props {
  holderId: string;
  dealId: string;
  dealName: string;
}

/** Display name for a contact, falling back to the email then a placeholder. */
function contactLabel(contact: FiasLeadsContactDto): string {
  const name = `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();
  return name || contact.primaryEmail || 'Unnamed contact';
}

/**
 * Substitutes `{{variable}}` placeholders in a template.
 *
 * Deliberately leaves unknown placeholders untouched rather than blanking
 * them: a visible `{{company}}` in the composer is an obvious prompt to fill
 * something in, whereas a silently-emptied one ships a sentence with a hole
 * in it to a customer.
 */
export function fillTemplate(body: string, values: Record<string, string>): string {
  return body.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, key: string) => {
    const value = values[key];
    return value ? value : match;
  });
}

export function SendDealEmailPanel({ holderId, dealId, dealName }: Props) {
  const [open, setOpen] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mailboxesQuery = useFiasLeadsMailboxes();
  const contactsQuery = useFiasLeadsContacts(holderId);
  const dealContactsQuery = useFiasLeadsDealContacts(holderId, open ? dealId : null);
  const templatesQuery = useFiasLeadsEmailTemplates(holderId);
  const sendEmail = useSendFiasLeadsDealEmail(holderId);

  const accounts = useMemo(() => mailboxesQuery.data ?? [], [mailboxesQuery.data]);
  const templates = templatesQuery.data ?? [];

  // Default to the primary mailbox once connections load.
  useEffect(() => {
    if (!accountId && accounts.length > 0) {
      setAccountId((accounts.find((a) => a.isPrimary) ?? accounts[0]).accountId);
    }
  }, [accounts, accountId]);

  // Contacts attached to the deal, resolved to addresses. This is the
  // "who would I be emailing about this deal" answer, so it seeds To.
  const dealContactEmails = useMemo(() => {
    const byId = new Map((contactsQuery.data ?? []).map((c) => [c.contactId, c]));
    // flatMap rather than filter+map: the contact may be missing from the
    // list (or have no address), and dropping both cases in one step keeps
    // the narrowing honest without a type predicate.
    return (dealContactsQuery.data ?? []).flatMap((dc) => {
      const contact = byId.get(dc.contactId);
      if (!contact?.primaryEmail) return [];
      return [{ email: contact.primaryEmail, label: contactLabel(contact) }];
    });
  }, [contactsQuery.data, dealContactsQuery.data]);

  // Prefill To the first time the deal's contacts arrive, but never
  // overwrite something the user has already typed.
  useEffect(() => {
    if (open && !to && dealContactEmails.length > 0) {
      setTo(dealContactEmails.map((c) => c.email).join(', '));
    }
  }, [open, to, dealContactEmails]);

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const template = templates.find((t) => t.templateId === id);
    if (!template) return;
    const firstContact = dealContactEmails[0]?.label ?? '';
    const values: Record<string, string> = {
      deal: dealName,
      deal_name: dealName,
      contact: firstContact,
      contact_name: firstContact,
    };
    setSubject(fillTemplate(template.subject, values));
    setBody(fillTemplate(template.bodyMarkdown, values));
  };

  /**
   * Splits the comma/semicolon-separated recipient field. Whitespace-only
   * fragments are dropped so a trailing comma doesn't produce an empty
   * address the server would reject.
   */
  const parseRecipients = (raw: string): Array<{ email: string }> =>
    raw
      .split(/[,;]/)
      .map((part) => part.trim())
      .filter(Boolean)
      .map((email) => ({ email }));

  const recipients = parseRecipients(to);
  const canSend =
    !!accountId && recipients.length > 0 && subject.trim().length > 0 && !sendEmail.isPending;

  const handleSend = async () => {
    setError(null);
    setNotice(null);
    try {
      const result = await sendEmail.mutateAsync({
        dealId,
        input: {
          accountId,
          to: recipients,
          subject: subject.trim(),
          text: body,
        },
      });
      // The send succeeded even if the timeline write didn't — say so
      // plainly instead of reporting a failure the user would act on by
      // sending the same mail again.
      setNotice(
        result.activityLogged
          ? 'Sent and logged on this deal.'
          : 'Sent — but it could not be logged on the deal timeline. Add a note so the history stays accurate.',
      );
      setTo('');
      setSubject('');
      setBody('');
      setTemplateId('');
      setOpen(false);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  if (!open) {
    return (
      <div className="flex flex-col gap-2">
        <button
          onClick={() => setOpen(true)}
          className="inline-flex w-fit items-center gap-2 rounded border border-border px-3 py-1.5 text-sm hover:bg-muted/30"
        >
          <Mail className="h-4 w-4" />
          Send email
        </button>
        {notice && <p className="text-xs text-muted-foreground">{notice}</p>}
      </div>
    );
  }

  if (mailboxesQuery.isLoading) {
    return (
      <div className="rounded border border-border bg-card p-4 text-sm">Loading mailboxes…</div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="rounded border border-border bg-card p-4 text-sm flex flex-col gap-2">
        <p className="font-medium">No mailbox connected</p>
        <p className="text-muted-foreground text-xs">
          Deal emails are sent from your own mailbox, so connect one in the Email arche first.
        </p>
        <div className="flex gap-2">
          <Link
            to="/a/arche_email/settings"
            className="rounded bg-primary px-3 py-1.5 text-primary-foreground text-xs"
          >
            Connect a mailbox
          </Link>
          <button
            onClick={() => setOpen(false)}
            className="rounded border border-border px-3 py-1.5 text-xs"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded border border-border bg-card p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-muted-foreground">Send email</h2>
        <button
          onClick={() => setOpen(false)}
          aria-label="Close composer"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid gap-3 text-sm">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">From</span>
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="rounded border border-border bg-background px-3 py-2"
          >
            {accounts.map((a) => (
              <option key={a.accountId} value={a.accountId}>
                {a.displayName ? `${a.displayName} <${a.emailAddress}>` : a.emailAddress}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">To</span>
          <input
            type="text"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="name@example.com, another@example.com"
            className="rounded border border-border bg-background px-3 py-2"
          />
          {dealContactEmails.length === 0 && (
            <span className="text-xs text-muted-foreground">
              No contacts with an email address on this deal — enter one above.
            </span>
          )}
        </label>

        {templates.length > 0 && (
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Template</span>
            <select
              value={templateId}
              onChange={(e) => applyTemplate(e.target.value)}
              className="rounded border border-border bg-background px-3 py-2"
            >
              <option value="">— Start from scratch —</option>
              {templates.map((t) => (
                <option key={t.templateId} value={t.templateId}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={2000}
            className="rounded border border-border bg-background px-3 py-2"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Message</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            className="rounded border border-border bg-background px-3 py-2"
          />
        </label>
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="flex items-center gap-2">
        <button
          onClick={handleSend}
          disabled={!canSend}
          className="inline-flex items-center gap-2 rounded bg-primary px-3 py-1.5 text-primary-foreground text-sm disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          {sendEmail.isPending ? 'Sending…' : 'Send'}
        </button>
        <button
          onClick={() => setOpen(false)}
          className="rounded border border-border px-3 py-1.5 text-sm"
        >
          Cancel
        </button>
        <span className="text-xs text-muted-foreground">
          Sent from your mailbox and logged on this deal.
        </span>
      </div>
    </div>
  );
}
